/* ─────────────────────────────────────────────────────────────
   Timbrica typing-trainer — Free Practice mode (v1.0 MVP)

   Single-file bundle for first ship:
     - TypingEngine: input handling, metrics, IME, state machine
     - Free Practice (time / words / quote / custom / zen)
     - Settings persistence via window.__toolSettings
     - Telemetry via window.__ae
     - Leaderboard + certificate submission (POST endpoints already
       served by TypingTrainerController)

   Will be split into ./typing-trainer/* modules in v1.1 — kept inline
   here so the MVP is one round-trip to load. ~600 lines.
   ───────────────────────────────────────────────────────────── */
(() => {
'use strict';

const SLUG = 'typing-trainer';
const I18N = window.TT_I18N || {};
const LOCALE = window.TT_LOCALE || 'en';
const T = (k, fallback) => (I18N[k] ?? fallback ?? k);

// ── Content (lazy-loaded from /data/typing/content-{lang}.json) ──
// Fallback minimal inline pool for first-paint before fetch completes.
// Full content (~150 words + 5-7 quotes per language) lives in the JSON
// files so the JS bundle stays small and SW can cache content per-locale.
const WORDS_INLINE_FALLBACK = {
    en: 'the be to of and a in that have it for not on with he as you do at this but his by from they we her she'.split(' '),
    ru: 'и в не на я что он с а как это по к но они так его все же бы было быть от уже мы или их еще для до меня вас'.split(' '),
    uk: 'і в не на я що він з а як це по до але вони так його все же би було бути від вже ми або їх ще для до мене вас ви',
    de: 'der die das und in ist nicht ein zu mit es auf für sich von dass den im eine sind als auch wenn was wir aus über bei nach so'.split(' '),
    es: 'el de la que en y a los se del las un por con una su para es al lo como más pero sus le ya o este sí porque esta entre'.split(' '),
    fr: 'le de un être et à les des en avoir que pour dans ce il qui ne sur se pas plus pouvoir par je avec tout faire son mettre autre on', uk: 'і в не на я що він з а як це по до але вони так його все ж би було бути від вже ми або їх ще для мене вас'.split(' '),
    pt: 'de a o que e do da em um para é com não uma os no se na por mais as dos como mas foi ao ele das tem à seu sua ou ser'.split(' '),
    id: 'yang dan di ini itu dari dengan untuk pada tidak akan adalah dalam atau juga oleh saya kami kita mereka anda dia ia bisa harus sudah masih telah'.split(' '),
    ko: '이 그 저 것 수 등 들 또 도 가 는 을 를 에 에서 으로 와 과 이다 있다 없다 하다 되다 되어 같다 같은 어떤 다른 모든 새로운 좋은 큰 작은'.split(' '),
    ar: 'في من إلى على هذا هذه ذلك تلك التي الذي مع عن قد كان كانت يكون تكون كل بعض ما لا ليس ليست هو هي هم هن أنا نحن'.split(' '),
    tr: 've bir bu şu o da de ki ile için gibi çok daha en ama fakat veya ya ben sen biz siz onlar var yok ne kim nasıl neden çünkü sonra önce şimdi burada nerede evet hayır belki tamam kadar kendi'.split(' '),
};

// Live pools — start with fallback, replaced when /data/typing/content-{lang}.json loads.
const WORDS = JSON.parse(JSON.stringify(WORDS_INLINE_FALLBACK));

// ── Quotes (fallback seed; full library lazy-loaded from JSON) ──
const QUOTES_INLINE_FALLBACK = {
    en: [
        { t: 'The only way to do great work is to love what you do.', src: 'Steve Jobs', len: 'short' },
        { t: 'In the middle of difficulty lies opportunity.', src: 'Albert Einstein', len: 'short' },
        { t: 'It is during our darkest moments that we must focus to see the light.', src: 'Aristotle', len: 'short' },
        { t: 'The future belongs to those who believe in the beauty of their dreams. Dreams are not what you see in your sleep — dreams are things that do not let you sleep, because you cannot rest until they are real.', src: 'Eleanor Roosevelt / Kalam', len: 'medium' },
        { t: 'Life is what happens when you are busy making other plans. Time you enjoy wasting is not wasted time. You will face many defeats in life but never let yourself be defeated.', src: 'John Lennon / Maya Angelou', len: 'medium' },
        { t: 'The greatest glory in living lies not in never falling, but in rising every time we fall. The way to get started is to quit talking and begin doing. Whether you think you can or you think you cannot, you are right. Twenty years from now you will be more disappointed by the things that you did not do than by the ones you did do.', src: 'Twain / Mandela', len: 'long' },
        { t: 'Whether you think you can or you think you cannot, you are right. Logic will get you from A to Z; imagination will get you everywhere. The best way to predict the future is to create it. The only impossible journey is the one you never begin. The man who moves a mountain begins by carrying away small stones. Quality means doing it right when no one is looking. Strive not to be a success, but rather to be of value. The true sign of intelligence is not knowledge but imagination. Try not to become a man of success but rather try to become a man of value.', src: 'Ford / Einstein / Drucker / Confucius', len: 'thicc' },
    ],
    ru: [
        { t: 'Главное, чтобы человек был хороший. Дела сами образуются.', src: 'Антон Чехов', len: 'short' },
        { t: 'Чем меньше любим мы женщину, тем легче нравимся мы ей.', src: 'А. С. Пушкин', len: 'short' },
        { t: 'Терпение и труд всё перетрут.', src: 'Народная мудрость', len: 'short' },
        { t: 'Привычка свыше нам дана: замена счастию она. Любви все возрасты покорны, её порывы благотворны. Я вас люблю — к чему лукавить? Но я другому отдана; я буду век ему верна.', src: 'А. С. Пушкин', len: 'medium' },
        { t: 'Учиться, учиться и ещё раз учиться. Самое сильное оружие в борьбе с собственной ленью — холодный душ и список конкретных задач. Чем тщательнее план, тем выше вероятность того, что план будет нарушен. Делай добро и бросай его в воду — оно к тебе вернётся непременно. Гений и злодейство — две вещи несовместные.', src: 'Народные пословицы и А. С. Пушкин', len: 'long' },
    ],
    uk: [{ t: 'Учітеся, брати мої, думайте, читайте, і чужому научайтесь, й свого не цурайтесь.', src: 'Тарас Шевченко', len: 'short' }],
    de: [{ t: 'Wer immer tut, was er schon kann, bleibt immer das, was er schon ist.', src: 'Henry Ford', len: 'short' }],
    es: [{ t: 'Lo poco que sé se lo debo a mi ignorancia.', src: 'Sacha Guitry', len: 'short' }],
    fr: [{ t: 'La vie est belle pour ceux qui savent la regarder.', src: 'Anonyme', len: 'short' }],
    pt: [{ t: 'A persistência é o caminho do êxito.', src: 'Charles Chaplin', len: 'short' }],
    id: [{ t: 'Pengalaman adalah guru terbaik dalam hidup.', src: 'Pepatah', len: 'short' }],
    ko: [{ t: '천 리 길도 한 걸음부터 시작된다.', src: '속담', len: 'short' }],
    ar: [{ t: 'من جدّ وجد ومن زرع حصد.', src: 'مثل عربي', len: 'short' }],
    tr: [{ t: 'Sabreden derviş muradına ermiş.', src: 'Türk atasözü', len: 'short' }],
};
const QUOTES = JSON.parse(JSON.stringify(QUOTES_INLINE_FALLBACK));

// Lazy-load full content per language. First call kicks fetch + caches result.
const contentLoadPromises = {};
async function loadContent(lang) {
    if (contentLoadPromises[lang]) return contentLoadPromises[lang];
    contentLoadPromises[lang] = (async () => {
        try {
            const res = await fetch(`/data/typing/content-${lang}.json`, { credentials: 'same-origin' });
            if (!res.ok) throw new Error(res.statusText);
            const data = await res.json();
            if (Array.isArray(data.words) && data.words.length) WORDS[lang] = data.words;
            if (Array.isArray(data.quotes) && data.quotes.length) QUOTES[lang] = data.quotes;
            return data;
        } catch (e) {
            // keep fallback — silently downgrade
            return null;
        }
    })();
    return contentLoadPromises[lang];
}

// ── Settings (defaults; overridden by __toolSettings) ──────────
// Layout follows locale: /ru/ → ЙЦУКЕН, /de/ → QWERTZ, /fr/ → AZERTY,
// everyone else → QWERTY. Without this, a Russian user on /ru/ sees Cyrillic
// text but a QWERTY virtual keyboard — they read 'q w e r' on screen while
// the actual physical keys produce 'й ц у к', so accuracy crashes to ~30%
// and the tool feels broken.
const LOCALE_TO_LAYOUT = { ru: 'jcuken', uk: 'jcuken_ua', de: 'qwertz', fr: 'azerty' };
const DEFAULTS = {
    layout: LOCALE_TO_LAYOUT[LOCALE] || 'qwerty',
    language: LOCALE === 'ru' ? 'ru' : (WORDS[LOCALE] ? LOCALE : 'en'),
    antiMistake: 'continue',
    showLiveWpm: '1',
    highlightWord: '1',
    submode: 'time',
    timeSec: '30',
    wordsN: '25',
    quoteLen: 'short',
};
const settings = { ...DEFAULTS };
function loadSettings() {
    const TS = window.__toolSettings;
    if (!TS) return;
    Object.keys(DEFAULTS).forEach(k => {
        const v = TS.load(SLUG, k, DEFAULTS[k]);
        if (v !== null && v !== undefined) settings[k] = v;
    });
}
function saveSetting(key, value) {
    settings[key] = value;
    window.__toolSettings?.save(SLUG, key, value);
}

// ── Telemetry helpers ───────────────────────────────────────────
function track(event, extra = {}) {
    if (window.__ae) window.__ae(event, { slug: SLUG, ...extra });
}
function trackCritical(event, extra = {}) {
    if (window.__aeCritical) window.__aeCritical(event, { slug: SLUG, ...extra });
    else track(event, extra);
}

// ── Typing engine ───────────────────────────────────────────────
const engine = {
    text: '',
    cursor: 0,
    typed: [],          // 0=correct, 1=wrong, char actually typed
    startedAt: 0,
    endsAt: 0,
    perSecondWpm: [],   // bucket array, 1 entry per second
    perKeyCorrect: {},  // {char: count}
    perKeyError: {},    // {char: count}
    keystrokes: [],     // [{t, ch, ok}] for replay capture (deltas computed later)
    running: false,
    ended: false,
    mode: 'free',
    submode: 'time',
    target: { kind: 'time', value: 30 }, // {kind, value}
    composingIME: false,
    lastSecondBucket: 0,
    onRefill: null,     // time-mode: () => string of extra words, set per session

    reset() {
        this.cursor = 0;
        this.typed.length = 0;
        this.startedAt = 0;
        this.endsAt = 0;
        this.perSecondWpm.length = 0;
        this.perKeyCorrect = {};
        this.perKeyError = {};
        this.keystrokes.length = 0;
        this.running = false;
        this.ended = false;
        this.composingIME = false;
        this.lastSecondBucket = 0;
        this.onRefill = null;
        hideKbdNote();
    },

    setText(text, target) {
        this.reset();
        this.text = text;
        this.target = target;
    },

    /** Type a single character. Returns true if session should end. */
    typeChar(ch) {
        if (this.ended) return false;
        // A keystroke the keyboard got wrong — another layout switched on, Caps
        // Lock — is not a typo: it is not scored, does not start the clock, and
        // the note under the stage says what to switch. 1395 of 17788 runs
        // (09–29.10.2026) ended under 20 % accuracy with exactly that signature.
        // Gate here, where every path (keydown, IME, soft keyboard) arrives.
        if (!keyboardStateLets(ch, this.text[this.cursor])) return false;
        if (!this.running) this._start();

        // Cursor reached the end of the buffer. A timed test must keep running
        // for its full duration, so pull in more words and re-render. Every
        // other mode (words/quote/custom) has already ended via the
        // end-condition below, and a lesson has no time budget — so a keystroke
        // past the buffer is a stray event we drop, rather than indexing
        // undefined text and crashing flashKeyPress (Sentry 5D:
        // `Cannot read properties of undefined (reading 'toLowerCase')`).
        if (this.cursor >= this.text.length) {
            if (this.target.kind === 'time' && this.onRefill) {
                this.text += this.onRefill();
                if (typeof renderText === 'function') renderText();
            } else {
                return false;
            }
        }

        const expected = this.text[this.cursor];
        const ok = (ch === expected);
        this.typed[this.cursor] = ok ? 0 : 1;
        this.keystrokes.push({ t: performance.now() - this.startedAt, ch, ok });
        if (typeof flashKeyPress === 'function') flashKeyPress(expected, ok);

        if (ok) {
            this.perKeyCorrect[expected] = (this.perKeyCorrect[expected] || 0) + 1;
            this.cursor++;
        } else {
            this.perKeyError[expected] = (this.perKeyError[expected] || 0) + 1;
            this._maybeEmitErrorBurst();
            if (settings.antiMistake === 'continue') {
                this.cursor++;
            }
            // 'stop' and 'requireFix' both stay put; difference is that
            // requireFix marks the slot as wrong so the cursor visually parks
            // here and the user must press Backspace before re-typing.
        }

        // End condition: any fixed-length session ends the moment the buffer
        // is exhausted. Timed sessions (kind 'time') are the sole exception —
        // they end on the clock in tick() and keep refilling their buffer
        // above, so they never satisfy this. This single invariant covers
        // EVERY fixed-text mode — words / quote / custom / lesson / adaptive /
        // code / race — without enumerating kinds. The old code enumerated
        // only words|quote|custom, silently omitting 'lesson': lessons never
        // auto-completed, so the results modal never opened, the next lesson
        // never unlocked, and live WPM decayed forever as loop() kept ticking
        // (chars frozen, elapsed growing). Reported by a user 2026-06-26;
        // broken since the tool's launch (the 'lesson' kind was never handled).
        if (this.target.kind !== 'time' && this.cursor >= this.text.length) {
            this._end();
            return true;
        }
        return false;
    },

    backspace() {
        if (this.cursor > 0 && this.typed[this.cursor - 1] !== undefined) {
            this.cursor--;
            this.typed[this.cursor] = undefined;
        }
    },

    _start() {
        this.startedAt = performance.now();
        if (this.target.kind === 'time') {
            this.endsAt = this.startedAt + this.target.value * 1000;
        }
        this.running = true;
        this._errBurstCount = 0;
        this._errBurstStart = 0;
        track('typing_session_start', {
            mode: this.mode, submode: this.submode,
            layout: settings.layout, language: settings.language,
            target_kind: this.target.kind, target_value: this.target.value,
        });
    },

    /** Detect "frustration" bursts: 5+ errors within 3 s — emit event so we
     *  can find lessons / content that are too hard. Called from typeChar
     *  on every error. */
    _maybeEmitErrorBurst() {
        const now = performance.now();
        if (now - this._errBurstStart > 3000) {
            this._errBurstStart = now;
            this._errBurstCount = 1;
        } else {
            this._errBurstCount++;
            if (this._errBurstCount === 5) {
                const m = this.metrics();
                track('typing_error_burst', {
                    mode: this.mode, submode: this.submode,
                    accuracy_so_far: m.accuracyPct,
                    chars_typed: m.chars,
                    layout: settings.layout, language: settings.language,
                });
            }
        }
    },

    _end() {
        if (this.ended) return;
        this.ended = true;
        this.running = false;
        const m = this.metrics();
        track('typing_session_complete', {
            mode: this.mode, submode: this.submode,
            wpm: m.wpm, accuracy: m.accuracyPct, consistency: m.consistencyPct ?? -1,
            duration: m.durationSec, chars: m.chars,
            layout: settings.layout, language: settings.language,
        });
    },

    tick(now) {
        if (!this.running || this.ended) return;
        if (this.target.kind === 'time' && now >= this.endsAt) {
            this._end();
        }
        // Sample per-second bucket
        const sec = Math.floor((now - this.startedAt) / 1000);
        if (sec > this.lastSecondBucket) {
            const sliceCorrect = this.typed.slice(0, this.cursor).filter(v => v === 0).length;
            const wpm = (sliceCorrect / 5) / Math.max(sec / 60, 1/60);
            this.perSecondWpm.push(wpm);
            this.lastSecondBucket = sec;
        }
    },

    metrics() {
        const elapsedMs = (this.endsAt && this.ended)
            ? (this.endsAt - this.startedAt)
            : (performance.now() - this.startedAt);
        const durationSec = Math.max(elapsedMs / 1000, 0.001);
        const correct = this.typed.slice(0, this.cursor).filter(v => v === 0).length;
        const wrong   = this.typed.slice(0, this.cursor).filter(v => v === 1).length;
        const total = correct + wrong;
        const wpm = (correct / 5) / (durationSec / 60);
        const cpm = correct / (durationSec / 60);
        const acc = total > 0 ? (100 * correct / total) : 100;
        let consistency = null;
        if (this.perSecondWpm.length >= 5) {
            const mean = this.perSecondWpm.reduce((a, b) => a + b, 0) / this.perSecondWpm.length;
            const variance = this.perSecondWpm.reduce((a, b) => a + (b - mean) ** 2, 0) / this.perSecondWpm.length;
            const stdev = Math.sqrt(variance);
            consistency = Math.max(0, Math.min(100, 100 * (1 - stdev / Math.max(mean, 1e-6))));
        }
        return {
            wpm: Math.round(wpm),
            cpm: Math.round(cpm),
            accuracyPct: Math.round(acc),
            consistencyPct: consistency !== null ? Math.round(consistency) : null,
            durationSec: Math.round(durationSec),
            chars: total,
            correct, wrong,
        };
    },
};

// ── Lessons (loaded lazily) ─────────────────────────────────────
const lessonsCache = {};
async function loadLessons(lang) {
    if (lessonsCache[lang]) return lessonsCache[lang];
    const url = `/data/typing/lessons-${lang}.json`;
    try {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        lessonsCache[lang] = data.lessons || [];
        return lessonsCache[lang];
    } catch (e) {
        // Fall back to EN if requested lang missing.
        if (lang !== 'en') return loadLessons('en');
        return [];
    }
}

function lessonsProgressKey(layout, lang) { return `tt-progress-lessons:${layout}:${lang}`; }
function loadLessonsProgress(layout, lang) {
    try {
        const raw = localStorage.getItem(lessonsProgressKey(layout, lang));
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}
let saveProgressTimer = 0;
function saveLessonsProgress(layout, lang, progress) {
    try { localStorage.setItem(lessonsProgressKey(layout, lang), JSON.stringify(progress)); } catch {}
    clearTimeout(saveProgressTimer);
    saveProgressTimer = setTimeout(() => syncProgressToCloud(layout, lang), 800);
}
async function syncProgressToCloud(layout, lang) {
    // Best-effort sync — silently skip if unauthenticated.
    const csrf = document.querySelector('meta[name=csrf-token]')?.content;
    if (!csrf) return;
    const state = { lessons: loadLessonsProgress(layout, lang) };
    try {
        await fetch('/api/typing-progress', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf, 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ layout, language: lang, state }),
        });
    } catch {}
}

// ── Virtual keyboard layouts ────────────────────────────────────
// Physical key positions stay constant (4 rows × N keys + space).
// Layout only swaps the displayed glyph. Finger zones are tied to position,
// not to glyph, so adaptive heatmap finger groupings remain consistent.
const LAYOUTS_KBD = {
    qwerty: [
        '`1234567890-=',
        'qwertyuiop[]\\',
        "asdfghjkl;'",
        'zxcvbnm,./',
    ],
    jcuken: [
        'ё1234567890-=',
        'йцукенгшщзхъ\\',
        'фывапролджэ',
        'ячсмитьбю.',
    ],
    // Украинская ЙЦУКЕН — не русская: ї вместо ъ, і вместо ы, є вместо э,
    // апостроф на месте ё, а ґ занимает клавишу обратного слэша.
    jcuken_ua: [
        "'1234567890-=",
        'йцукенгшщзхїґ',
        'фівапролджє',
        'ячсмитьбю.',
    ],
    dvorak: [
        '`1234567890[]',
        "',.pyfgcrl/=\\",
        "aoeuidhtns-",
        ';qjkxbmwvz',
    ],
    colemak: [
        '`1234567890-=',
        'qwfpgjluy;[]\\',
        "arstdhneio'",
        'zxcvbkm,./',
    ],
    workman: [
        '`1234567890-=',
        'qdrwbjfup;[]\\',
        "ashtgyneoi'",
        'zxmcvkl,./',
    ],
    azerty: [
        '²&é"\'(-è_çà)=',
        'azertyuiop^$',
        'qsdfghjklmù',
        'wxcvbn,;:!',
    ],
    qwertz: [
        '^1234567890ß´',
        'qwertzuiopü+',
        'asdfghjklöä',
        'yxcvbnm,.-',
    ],
};

// Finger map by [row, col] — fixed physical positions matching FINGER_BY_KEY_QWERTY logic.
// LP = left pinky, LR = left ring, LM = left middle, LI = left index,
// TH = thumbs, RI = right index, RM = right middle, RR = right ring, RP = right pinky.
const FINGER_GRID = [
    // row 0 (digit row, 13 cols): LP LP LR LM LI LI RI RI RM RR RP RP RP
    ['LP','LP','LR','LM','LI','LI','RI','RI','RM','RR','RP','RP','RP'],
    // row 1 (top, 13 cols): LP LR LM LI LI RI RI RM RR RP RP RP RP
    ['LP','LR','LM','LI','LI','RI','RI','RM','RR','RP','RP','RP','RP'],
    // row 2 (home, 11 cols): LP LR LM LI LI RI RI RM RR RP RP
    ['LP','LR','LM','LI','LI','RI','RI','RM','RR','RP','RP'],
    // row 3 (bottom, 10 cols): LP LR LM LI LI RI RI RM RR RP
    ['LP','LR','LM','LI','LI','RI','RI','RM','RR','RP'],
];

function renderKeyboard() {
    const root = $('[data-tt-keyboard]');
    if (!root) return;
    const layout = LAYOUTS_KBD[settings.layout] || LAYOUTS_KBD.qwerty;
    const html = [];
    for (let r = 0; r < 4; r++) {
        const rowChars = [...(layout[r] || '')];
        const fingers = FINGER_GRID[r] || [];
        const cells = rowChars.map((ch, c) => {
            const f = fingers[c] || '';
            return `<button class="tt-key" data-key="${ch}" data-row="${r}" data-col="${c}" data-finger="${f}" aria-hidden="true" tabindex="-1">${ch === ' ' ? '␣' : ch}</button>`;
        }).join('');
        html.push(`<div class="tt-kbd-row" data-row="${r}">${cells}</div>`);
    }
    // Space row
    html.push(`<div class="tt-kbd-row"><button class="tt-key wider" data-key=" " data-finger="TH" aria-hidden="true" tabindex="-1">␣</button></div>`);
    root.innerHTML = html.join('');
}

// The on-screen keyboard is a picture of the keys, not a control: a click on a
// key (people do — rage clicks on the highlighted «next» key, 09–29.10) must not
// take the focus away from the typing field, and an aria-hidden element must
// not be focusable at all (hence tabindex -1 above).
function keepFocusOffTheKeys(e) {
    if (e.target && e.target.closest && e.target.closest('.tt-key')) e.preventDefault();
}

function highlightNextKey() {
    const root = $('[data-tt-keyboard]');
    if (!root) return;
    // Clear previous
    root.querySelectorAll('.tt-key.next').forEach(k => k.classList.remove('next'));
    if (engine.ended || !engine.text) return;
    const nextCh = engine.text[engine.cursor];
    if (!nextCh) return;
    const lc = nextCh.toLowerCase();
    const key = root.querySelector(`.tt-key[data-key="${cssEscape(lc)}"]`)
              || root.querySelector(`.tt-key[data-key="${cssEscape(nextCh)}"]`);
    key?.classList.add('next');
}

function cssEscape(s) {
    if (window.CSS?.escape) return CSS.escape(s);
    return s.replace(/["\\]/g, '\\$&');
}

function flashKeyPress(ch, ok) {
    const root = $('[data-tt-keyboard]');
    if (!root) return;
    const lc = ch.toLowerCase();
    const key = root.querySelector(`.tt-key[data-key="${cssEscape(lc)}"]`)
              || root.querySelector(`.tt-key[data-key="${cssEscape(ch)}"]`);
    if (!key) return;
    key.classList.add(ok ? 'pressed' : 'error');
    setTimeout(() => key.classList.remove('pressed', 'error'), 140);
}

// ── DOM helpers ─────────────────────────────────────────────────
const $  = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

const dom = {};

function pickRandom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function makeWordList(lang, n) {
    const pool = WORDS[lang] || WORDS.en;
    const out = [];
    for (let i = 0; i < n; i++) out.push(pickRandom(pool));
    return out.join(' ');
}

function makeQuoteText(lang, len) {
    const pool = (QUOTES[lang] || QUOTES.en).filter(q => q.len === len);
    if (!pool.length) return (QUOTES[lang] || QUOTES.en)[0]?.t || makeWordList(lang, 25);
    return pickRandom(pool).t;
}

function makeTimeText(lang, seconds) {
    // Approximate: 80 chars/word, 80 WPM ceiling → seconds * 80 / 60 words * 1.5 buffer.
    const n = Math.max(60, Math.ceil(seconds * 4));
    return makeWordList(lang, n);
}

// ── Render typing text into spans ───────────────────────────────
function renderText() {
    const root = dom.text;
    root.innerHTML = '';
    const frag = document.createDocumentFragment();
    let wordStart = 0;
    let wordEl = document.createElement('span');
    wordEl.className = 'word';
    for (let i = 0; i < engine.text.length; i++) {
        const ch = engine.text[i];
        const isSpace = ch === ' ';
        if (ch === '\n') {
            // Newline (code-mode snippets): close the word, show a dim ↵ glyph
            // as the expected char, then a real line break so multi-line code
            // reads correctly. The glyph keeps a .ch cell so refreshCharStates
            // colours it like any other expected char (typed via Enter).
            frag.appendChild(wordEl);
            const nl = document.createElement('span');
            nl.className = 'ch nl';
            nl.dataset.idx = String(i);
            nl.textContent = '↵';
            frag.appendChild(nl);
            frag.appendChild(document.createElement('br'));
            wordEl = document.createElement('span');
            wordEl.className = 'word';
            wordStart = i + 1;
        } else if (isSpace) {
            // close previous word
            frag.appendChild(wordEl);
            const sp = document.createElement('span');
            sp.className = 'ch sp';
            sp.dataset.idx = String(i);
            sp.textContent = ' ';
            frag.appendChild(sp);
            wordEl = document.createElement('span');
            wordEl.className = 'word';
            wordStart = i + 1;
        } else {
            const s = document.createElement('span');
            s.className = 'ch upcoming';
            s.dataset.idx = String(i);
            s.textContent = ch;
            wordEl.appendChild(s);
        }
    }
    frag.appendChild(wordEl);
    root.appendChild(frag);
    refreshCharStates();
}

function refreshCharStates() {
    highlightNextKey();
    const spans = $$('.ch', dom.text);
    for (const s of spans) {
        const i = +s.dataset.idx;
        s.classList.remove('correct', 'wrong', 'current', 'upcoming');
        const typed = engine.typed[i];
        if (typed === 0) s.classList.add('correct');
        else if (typed === 1) s.classList.add('wrong');
        else if (i === engine.cursor) s.classList.add('current');
        else s.classList.add('upcoming');
    }
    if (settings.highlightWord === '1') {
        $$('.word.active', dom.text).forEach(w => w.classList.remove('active'));
        let curWord = null;
        let acc = 0;
        for (const word of $$('.word', dom.text)) {
            const len = word.querySelectorAll('.ch').length;
            if (engine.cursor >= acc && engine.cursor < acc + len + 1) {
                curWord = word;
                break;
            }
            acc += len + 1; // +1 for the space
        }
        curWord?.classList.add('active');
    }
}

// ── Stats UI ────────────────────────────────────────────────────
// Central writer for the [data-stat=…] readouts. The refreshStats
// loop runs every requestAnimationFrame tick, so if a stat element
// is missing from the DOM (results modal mutates layout, userscript
// strips it, or the rAF fires once after a teardown) the raw
// .textContent write crashes the entire loop (TIMBRICA-FRONTEND-88).
// One guard at the boundary instead of 6 at the call sites.
function setStat(name, text){
    const el = $('[data-stat=' + name + ']');
    if (el) el.textContent = text;
}
function refreshStats() {
    const m = engine.metrics();
    if (settings.showLiveWpm === '1' || engine.ended) {
        setStat('wpm', String(m.wpm));
        setStat('cpm', String(m.cpm));
    } else {
        setStat('wpm', '—');
        setStat('cpm', '—');
    }
    setStat('accuracy', m.accuracyPct + '%');
    setStat('consistency', m.consistencyPct !== null ? (m.consistencyPct + '%') : '—');

    // Time/words counter
    const label = $('[data-stat-label="timeOrWords"]');
    const val = $('[data-stat=timeOrWords]');
    if (!label || !val) return;
    if (engine.target.kind === 'time') {
        label.textContent = T('time_left', 'Time left');
        const remainingMs = Math.max(0, engine.endsAt - performance.now());
        val.textContent = engine.running ? Math.ceil(remainingMs / 1000) + 's' : engine.target.value + 's';
    } else {
        label.textContent = T('chars', 'Chars');
        val.textContent = engine.cursor + ' / ' + engine.text.length;
    }
}

// ── Results modal ───────────────────────────────────────────────
function rankFor(wpm) {
    if (wpm < 20) return T('rank_snail', 'Snail') + ' 🐌';
    if (wpm < 35) return T('rank_turtle', 'Turtle') + ' 🐢';
    if (wpm < 55) return T('rank_average', 'Average') + ' 🚶';
    if (wpm < 75) return T('rank_fast', 'Fast') + ' 🏃';
    if (wpm < 95) return T('rank_pro', 'Pro') + ' 🚀';
    return T('rank_ninja', 'Ninja') + ' 🥷';
}

let lastResultMetrics = null;
function openResults() {
    if (dom.resultsDialog?.open) return; // prevent double-open
    const m = engine.metrics();
    lastResultMetrics = m;
    $('[data-results-wpm]').textContent = String(m.wpm);
    $('[data-results-rank]').textContent = rankFor(m.wpm);
    $('[data-results-cpm]').textContent = String(m.cpm);
    $('[data-results-accuracy]').textContent = m.accuracyPct + '%';
    $('[data-results-consistency]').textContent = m.consistencyPct !== null ? (m.consistencyPct + '%') : '—';
    $('[data-results-time]').textContent = m.durationSec + 's';

    // Banner + next-lesson CTA — hidden by default; onLessonComplete re-shows
    // them when a lesson is finished.
    const banner = $('[data-results-lesson-banner]');
    if (banner) banner.hidden = true;
    const nextBtn = $('[data-tt-next-lesson]');
    if (nextBtn) nextBtn.hidden = true;

    // Mode-specific finalisation
    if (engine.mode === 'lessons' && currentLessonId) {
        onLessonComplete(m);
    } else if (engine.mode === 'adaptive') {
        onAdaptiveComplete(m);
    } else if (engine.mode === 'race') {
        onRaceComplete(m);
    } else if (engine.mode === 'code') {
        track('typing_code_complete', { wpm: m.wpm, accuracy: m.accuracyPct, code_lang: settings.codeLang || 'js' });
    }

    // Universal completion signal — drives the site-wide tool_complete funnel
    // (session_start → tool_complete) exactly like every other tool. Fires
    // once per finished session (the open-guard above prevents re-entry), so
    // success rate is finally measurable. Before this, the trainer never
    // emitted tool_complete and the funnel read as ~0 % completion.
    try { window.__aeToolComplete?.({ mode: engine.mode, wpm: m.wpm, accuracy: m.accuracyPct }); } catch (_) {}

    // Heatmap (deferred to v1.1 — keystone differentiator)
    renderHeatmap(m);

    const dlg = dom.resultsDialog;
    if (dlg && typeof dlg.showModal === 'function') {
        try { dlg.showModal(); } catch (_) { dlg.setAttribute('open', 'open'); }
    }

    // Submit to leaderboard (best-effort)
    submitLeaderboard(m).catch(() => { /* swallow — Background Sync wires in v1.1 */ });

    // Certificate eligibility
    const certBtn = $('[data-tt-get-certificate]');
    if (certBtn) {
        const eligible = m.wpm >= 60 && m.accuracyPct >= 95 && m.durationSec >= 60 && ['free','lessons','adaptive','code'].includes(engine.mode);
        certBtn.hidden = !eligible;
    }

    // Screen-reader announcement
    if (window.__announce) window.__announce(`${m.wpm} ${T('wpm','WPM')}, ${m.accuracyPct}% ${T('accuracy','accuracy')}`);
}

// ── Heatmap (per-key + per-finger) ──────────────────────────────
// Finger map for QWERTY (we'll wire other layouts in v1.1).
const FINGER_BY_KEY_QWERTY = {
    '`':'LP','1':'LP','q':'LP','a':'LP','z':'LP',
    '2':'LR','w':'LR','s':'LR','x':'LR',
    '3':'LM','e':'LM','d':'LM','c':'LM',
    '4':'LI','5':'LI','r':'LI','t':'LI','f':'LI','g':'LI','v':'LI','b':'LI',
    ' ':'TH',
    '6':'RI','7':'RI','y':'RI','u':'RI','h':'RI','j':'RI','n':'RI','m':'RI',
    '8':'RM','i':'RM','k':'RM',',':'RM',
    '9':'RR','o':'RR','l':'RR','.':'RR',
    '0':'RP','-':'RP','=':'RP','p':'RP','[':'RP',']':'RP','\\':'RP',';':'RP','\'':'RP','/':'RP',
};
const FINGER_BY_KEY_JCUKEN = {
    'й':'LP','ц':'LR','у':'LM','к':'LI','е':'LI',
    'ф':'LP','ы':'LR','в':'LM','а':'LI','п':'LI',
    'я':'LP','ч':'LR','с':'LM','м':'LI','и':'LI',
    ' ':'TH',
    'н':'RI','г':'RI','т':'RI','ь':'RI',
    'ш':'RM','о':'RM','б':'RM',
    'щ':'RR','л':'RR','ю':'RR',
    'з':'RP','х':'RP','ъ':'RP','д':'RP','ж':'RP','э':'RP','.':'RP',
};
const FINGER_NAMES = { LP:'Pinky L', LR:'Ring L', LM:'Middle L', LI:'Index L', TH:'Thumbs', RI:'Index R', RM:'Middle R', RR:'Ring R', RP:'Pinky R' };

function fingerForKey(ch) {
    const lc = ch.toLowerCase();
    return FINGER_BY_KEY_JCUKEN[lc] || FINGER_BY_KEY_QWERTY[lc] || null;
}

function renderHeatmap(m) {
    const wrap = $('[data-results-heatmap]');
    if (!wrap) return;
    // Per-key cells — only keys with samples ≥ 1
    const keys = new Set([...Object.keys(engine.perKeyCorrect || {}), ...Object.keys(engine.perKeyError || {})]);
    const cells = [];
    for (const k of keys) {
        if (k.length !== 1 || k === ' ') continue;
        const c = engine.perKeyCorrect[k] || 0;
        const e = engine.perKeyError[k] || 0;
        const total = c + e;
        if (total < 1) continue;
        const errRate = e / total;
        const hue = Math.max(0, Math.min(120, 120 * (1 - errRate * 2)));
        const display = k === ' ' ? '␣' : k;
        cells.push(`<span class="tt-hm-cell" style="background:hsl(${hue} 60% 35%)" title="${display}: ${e}/${total} errors">${display}</span>`);
    }
    $('[data-results-heatmap-cells]').innerHTML = cells.join('') || '<span style="color:var(--text-muted);font-size:12px">No data — type more characters</span>';

    // Per-finger bars
    const perFinger = {};
    for (const f of Object.values(FINGER_NAMES)) perFinger[f] = { correct: 0, error: 0 };
    for (const k of keys) {
        const f = fingerForKey(k);
        if (!f) continue;
        const fname = FINGER_NAMES[f];
        if (!fname) continue;
        perFinger[fname].correct += engine.perKeyCorrect[k] || 0;
        perFinger[fname].error += engine.perKeyError[k] || 0;
    }
    const fingerRows = Object.entries(perFinger).map(([fname, { correct, error }]) => {
        const total = correct + error;
        const errRate = total ? error / total : 0;
        const errBarPct = Math.min(100, errRate * 200);
        const samples = total ? `${total}` : '0';
        return `
        <div class="tt-finger-row">
            <span class="tt-finger-label">${fname}</span>
            <div class="tt-finger-bar"><div class="tt-finger-bar-fill" style="width:${errBarPct}%"></div></div>
            <span class="tt-finger-pct">${total ? Math.round(errRate * 100) + '% err' : '—'}</span>
            <span class="tt-finger-samples">${samples}</span>
        </div>`;
    }).join('');
    $('[data-results-fingers]').innerHTML = fingerRows;
}

// ── Leaderboard submit ──────────────────────────────────────────
async function submitLeaderboard(m) {
    if (!m.correct || m.durationSec < 10) return;
    const csrf = document.querySelector('meta[name=csrf-token]')?.content;
    // Determine lesson_passed for lessons mode
    let lessonPassed = false;
    if (engine.mode === 'lessons' && currentLessonId) {
        const lessons = lessonsCache[settings.language] || [];
        const lesson = lessons.find(l => l.id === currentLessonId);
        if (lesson && m.wpm >= lesson.target_wpm && m.accuracyPct >= lesson.target_acc) {
            lessonPassed = true;
        }
    }
    // Determine ghost_won for race mode
    let ghostWon = false;
    if (engine.mode === 'race' && raceGhost) {
        // We won if we finished AND have higher wpm than ghost (or equal during photo-finish)
        if (m.wpm >= (raceGhost.wpm || 0)) ghostWon = true;
    }
    const body = {
        wpm: m.wpm,
        accuracy_pct: m.accuracyPct,
        consistency_pct: m.consistencyPct,
        duration_sec: m.durationSec,
        chars: m.chars,
        correct: m.correct,
        wrong: m.wrong,
        layout: settings.layout,
        language: settings.language,
        mode: engine.mode || 'free',
        lesson_passed: lessonPassed,
        ghost_won: ghostWon,
    };
    try {
        const res = await fetch('/api/typing-score', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf, 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            console.warn('[typing-trainer] leaderboard submit failed', await res.text());
            return;
        }
        const data = await res.json();
        // Surface unlocked achievements (logged-in users only)
        if (Array.isArray(data.unlocked) && data.unlocked.length && window.__toast) {
            for (const a of data.unlocked) {
                const name = a?.achievement?.name_key || a?.name || a?.slug || 'Achievement';
                window.__toast(`🏆 ${name}`, 'success');
                track('typing_achievement_unlocked', { slug: a?.slug || a?.achievement?.slug });
            }
        }
    } catch (e) {
        console.warn('[typing-trainer] leaderboard submit error', e);
    }
}

// ── Keyboard state (public/js/keyboard-state.js) ────────────────
// Whose keystroke was it — the person's, or the keyboard's (another layout,
// Caps Lock)? The latter is held back and named in the note under the stage;
// while the note is up, only a letter of the text's own writing system shows
// the keyboard is fixed. No KeyboardState (script failed) → score as before.
let kbdCaps = false;
let kbdNote = '';
function keyboardStateLets(ch, expected) {
    const KS = window.KeyboardState;
    if (!KS || expected === undefined) return true;
    const miss = KS.miss(ch, expected, kbdCaps);
    if (miss) { showKbdNote(miss); return false; }
    if (kbdNote) {
        if (!KS.fixes(ch, expected)) return false;
        hideKbdNote();
    }
    return true;
}
function showKbdNote(kind) {
    if (kbdNote === kind || !dom.kbdNote) return;
    kbdNote = kind;
    dom.kbdNote.textContent = T(kind === 'layout' ? 'layout_hint' : 'caps_hint');
    dom.kbdNote.hidden = false;
    track('typing_kbd_hint', { kind, mode: engine.mode, running: engine.running ? 1 : 0 });
}
function hideKbdNote() {
    if (!kbdNote) return;
    kbdNote = '';
    if (dom.kbdNote) dom.kbdNote.hidden = true;
}

// ── Input handling ──────────────────────────────────────────────
let rafId = 0;
function ensureFocus() {
    dom.input.focus({ preventScroll: true });
}

// The pre-primitive answer, kept as the fallback when timbrica-core is absent:
// any button, link, select, field or tabindex element counts as a control.
function isControlTarget(tgt) {
    if (!tgt || tgt === dom.input || tgt === dom.stage) return false;
    if (tgt === document.body || tgt === document.documentElement) return false;
    const tag = tgt.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON'
        || tag === 'A' || tag === 'SUMMARY' || tag === 'OPTION' || tag === 'LABEL') return true;
    if (tgt.isContentEditable) return true;
    if (typeof tgt.getAttribute === 'function' && tgt.getAttribute('role') === 'button') return true;
    if (typeof tgt.hasAttribute === 'function' && tgt.hasAttribute('tabindex')) return true;
    return false;
}

// Is the key someone else's? The trainer's own surfaces — the overlay
// textarea, the stage, the bare page (focus drifts to <body> when ads or a
// closed dialog steal it) — always type. Anything else keeps a key only when
// the key has work there (window.__keyOwnedByFocus): a field or a select takes
// letters; a button, a tab, «Show keyboard» take Space/Enter only under
// keyboard focus, so WCAG 2.1.1 holds. Deciding by tag gave every key to a
// button clicked with the mouse: after a click on 50 of 60 controls (the
// on-screen keys, «Show keyboard», ⚙ after its dialog closed) typing stayed dead
// until the stage was clicked (stand 29.10; 156 slow keydowns on a button in
// 20 days of telemetry).
function keyOwnedElsewhere(ev) {
    const tgt = ev.target;
    if (!tgt || tgt === dom.input || tgt === dom.stage || tgt === document.body || tgt === document.documentElement) return false;
    if (typeof window.__keyOwnedByFocus === 'function') return window.__keyOwnedByFocus(ev);
    return isControlTarget(tgt);
}

function onKeydown(e) {
    if (e.getModifierState) kbdCaps = e.getModifierState('CapsLock');
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (keyOwnedElsewhere(e)) return;
    if (engine.ended) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startSession(); }
        return;
    }
    if (e.key === 'Escape') {
        e.preventDefault();
        startSession();
        return;
    }
    if (e.key === 'Backspace') {
        if (settings.antiMistake !== 'requireFix') return; // ignore unless required
        e.preventDefault();
        engine.backspace();
        refreshCharStates();
        scrollCaretIntoView();
        return;
    }
    if (e.key === 'Enter') {
        // Enter types the newline that code-mode multi-line snippets require
        // (e.key 'Enter' is not length-1, so it never reached typeChar before —
        // code sessions were unfinishable, stuck at the first line break). In
        // single-line modes the expected char is never '\n', so we swallow
        // Enter (no stray form-submit / scroll) without scoring a miss.
        e.preventDefault();
        if (engine.text[engine.cursor] === '\n') {
            const ended = engine.typeChar('\n');
            refreshCharStates();
            scrollCaretIntoView();
            if (ended) openResults();
        }
        return;
    }
    // NB: Tab is intentionally NOT intercepted — trapping it is a WCAG 2.1.2
    // keyboard-trap violation, and no mode needs a literal tab character (code
    // snippets indent with spaces). Tab traverses focus out of the stage; the
    // user clicks/taps the stage to resume typing.
    if (e.key && e.key.length === 1) {
        if (engine.composingIME) return;
        e.preventDefault();
        lastKeydownAt = performance.now();
        const ended = engine.typeChar(e.key);
        refreshCharStates();
        scrollCaretIntoView();
        if (ended) openResults();
    }
}

function onCompositionStart() { engine.composingIME = true; }
function onCompositionEnd(e) {
    engine.composingIME = false;
    const data = (e.data || '').toString();
    for (const ch of data) engine.typeChar(ch);
    refreshCharStates();
    if (engine.ended) openResults();
    scrollCaretIntoView();
    if (dom.input) dom.input.value = '';
}

// Track last keydown timestamp so we can ignore the `input` event that
// follows it (otherwise on Android/Chromium each char gets counted twice —
// once via keydown, once via input — torching accuracy to ~20 %).
let lastKeydownAt = 0;
function onInputFallback(e) {
    // Always clear the value so the next composition starts clean.
    const v = e.target.value || '';
    e.target.value = '';
    if (engine.composingIME) return;
    // If a keydown handler already consumed these chars (within last 60ms),
    // skip them to avoid double-typing.
    if (performance.now() - lastKeydownAt < 60) return;
    if (!v) return;
    for (const ch of v) engine.typeChar(ch);
    refreshCharStates();
    scrollCaretIntoView();
    if (engine.ended) openResults();
}

// Smoothly scroll the active char into the visible portion of .tt-stage.
// Uses block:'nearest' per CLAUDE.md "No Auto-Scroll" rule (no page jumps).
function scrollCaretIntoView() {
    const cur = dom.text?.querySelector('.ch.current');
    if (!cur) return;
    try { cur.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }); } catch (_) {
        cur.scrollIntoView(false);
    }
}

// ── Main loop ───────────────────────────────────────────────────
function loop() {
    const now = performance.now();
    engine.tick(now);
    refreshStats();
    if (!engine.ended) rafId = requestAnimationFrame(loop);
    else openResults();
}

// ── Lessons UI ─────────────────────────────────────────────────
let currentLessonId = null;
let nextLessonId = null; // set by onLessonComplete when a passed lesson has a successor
async function renderLessonsGrid() {
    const root = $('[data-lessons-grid]');
    if (!root) return;
    root.innerHTML = '<div style="text-align:center;color:var(--text-muted);padding:24px">…</div>';
    const lessons = await loadLessons(settings.language);
    const progress = loadLessonsProgress(settings.layout, settings.language);
    const tiles = lessons.map((l, idx) => {
        const prev = idx === 0 ? null : lessons[idx - 1];
        const prevPassed = !prev || progress[prev.id]?.passed;
        const own = progress[l.id] || {};
        const locked = !prevPassed;
        const passed = !!own.passed;
        const bestWpm = own.best_wpm || 0;
        const attempts = own.attempts || 0;
        return `
        <button class="tt-lesson-tile lg-card${locked ? ' locked' : ''}${passed ? ' passed' : ''}" data-lesson-id="${l.id}" ${locked ? 'disabled' : ''}>
            <span class="tt-lesson-num">${locked ? '🔒' : (passed ? '✓' : l.id)}</span>
            <span class="tt-lesson-name">${l.name}</span>
            <span class="tt-lesson-meta">${l.target_wpm} WPM · ${l.target_acc}%${bestWpm ? ` · best ${bestWpm}` : ''}${attempts ? ` · ${attempts}x` : ''}</span>
        </button>`;
    }).join('');
    root.innerHTML = tiles;
    root.querySelectorAll('.tt-lesson-tile').forEach(btn => {
        btn.addEventListener('click', () => {
            const id = +btn.dataset.lessonId;
            if (btn.hasAttribute('disabled')) return;
            startLesson(id);
        });
    });
}

async function startLesson(id) {
    const lessons = await loadLessons(settings.language);
    const lesson = lessons.find(l => l.id === id);
    if (!lesson) return;
    currentLessonId = id;
    cancelAnimationFrame(rafId);
    engine.mode = 'lessons';
    engine.submode = `lesson-${id}`;
    engine.setText(lesson.pattern, { kind: 'lesson', value: id, lesson });
    // Switch UI: hide grid, show shared stage + lesson banner
    $('[data-lessons-grid-wrap]').hidden = true;
    $('[data-lesson-active-banner]').hidden = false;
    $('[data-tt-stage]').hidden = false;
    $('[data-lesson-active-name]').textContent = `${lesson.id}. ${lesson.name}`;
    $('[data-lesson-active-target]').textContent = `${lesson.target_wpm} WPM · ${lesson.target_acc}%`;
    renderText();
    refreshStats();
    dom.overlay?.classList.add('hidden');
    ensureFocus();
    rafId = requestAnimationFrame(loop);
    track('typing_lesson_start', { lesson_id: id, layout: settings.layout, language: settings.language });
}

function backToLessonsGrid() {
    cancelAnimationFrame(rafId);
    currentLessonId = null;
    engine.ended = true;
    $('[data-lessons-grid-wrap]').hidden = false;
    $('[data-lesson-active-banner]').hidden = true;
    $('[data-tt-stage]').hidden = true;
    renderLessonsGrid();
}

function onLessonComplete(m) {
    if (!currentLessonId) return;
    const lessons = lessonsCache[settings.language] || [];
    const lesson = lessons.find(l => l.id === currentLessonId);
    if (!lesson) return;
    const passed = m.wpm >= lesson.target_wpm && m.accuracyPct >= lesson.target_acc;
    const progress = loadLessonsProgress(settings.layout, settings.language);
    const cur = progress[currentLessonId] || { passed: false, best_wpm: 0, attempts: 0, last_at: 0 };
    cur.attempts++;
    cur.last_at = Date.now();
    if (m.wpm > cur.best_wpm) cur.best_wpm = m.wpm;
    if (passed) cur.passed = true;
    progress[currentLessonId] = cur;
    saveLessonsProgress(settings.layout, settings.language, progress);
    track('typing_lesson_complete', {
        lesson_id: currentLessonId, passed,
        wpm: m.wpm, accuracy: m.accuracyPct,
        layout: settings.layout, language: settings.language,
    });
    // Show results modal with pass/fail banner
    const banner = $('[data-results-lesson-banner]');
    if (banner) {
        banner.hidden = false;
        if (passed) {
            banner.className = 'tt-results-banner ok';
            banner.textContent = T('lesson_pass_msg', '🎉 Lesson passed! You unlocked the next one.');
        } else {
            banner.className = 'tt-results-banner fail';
            const msg = (T('lesson_fail_msg', 'Almost. Aim for :wpm WPM at :acc% accuracy.')
                .replace(':wpm', lesson.target_wpm).replace(':acc', lesson.target_acc));
            banner.textContent = msg;
        }
    }

    // One-click jump to the next lesson once this one is cleared. The core of
    // the "second level won't open" report: passing a lesson must lead
    // somewhere. Show the CTA only when passed AND a next lesson exists (it is
    // now unlocked — progress.passed was saved above, which is exactly what
    // renderLessonsGrid() reads to unlock the following tile).
    nextLessonId = null;
    const nextBtn = $('[data-tt-next-lesson]');
    if (passed) {
        const idx = lessons.findIndex(l => l.id === currentLessonId);
        const next = idx >= 0 ? lessons[idx + 1] : null;
        if (next) {
            nextLessonId = next.id;
            if (nextBtn) nextBtn.hidden = false;
        }
    }
}

// ── Session start / restart ─────────────────────────────────────
function startSession() {
    cancelAnimationFrame(rafId);
    const lang = settings.language;
    let target, text;
    if (settings.submode === 'time') {
        target = { kind: 'time', value: Math.max(5, parseInt(settings.timeSec, 10) || 30) };
        text = makeTimeText(lang, target.value);
    } else if (settings.submode === 'words') {
        target = { kind: 'words', value: Math.max(1, parseInt(settings.wordsN, 10) || 25) };
        text = makeWordList(lang, target.value);
    } else if (settings.submode === 'quote') {
        target = { kind: 'quote', value: settings.quoteLen };
        text = makeQuoteText(lang, settings.quoteLen);
    } else if (settings.submode === 'custom') {
        const ta = $('[data-custom-text]');
        const v = (ta?.value || '').trim();
        text = v || makeWordList(lang, 25);
        target = { kind: 'custom', value: text.length };
    } else {
        // zen: no time limit, no end (target = words but length 9999)
        text = makeWordList(lang, 200);
        target = { kind: 'words', value: 200 };
    }
    engine.mode = 'free';
    engine.submode = settings.submode;
    engine.setText(text, target);
    // A timed test ends on the clock, not on the buffer — keep it fed with
    // fresh words so a fast typist can never run off the end of the text.
    if (target.kind === 'time') {
        engine.onRefill = () => ' ' + makeWordList(lang, 40);
    }
    renderText();
    refreshStats();
    dom.overlay?.classList.add('hidden');
    dom.stage?.classList.add('focused');
    ensureFocus();
    rafId = requestAnimationFrame(loop);
}

// ── Adaptive mode (Keybr-style with bigrams) ────────────────────
const ADAPTIVE_LETTERS_EN = ['e','t','a','o','i','n','s','r','h','l','d','c','u','m','f','p','g','w','y','b','v','k','x','j','q','z'];
const ADAPTIVE_LETTERS_RU = ['о','е','а','и','н','т','с','р','в','л','к','м','д','п','у','я','ы','ь','г','з','б','ч','й','х','ж','ю','ш','ц','щ','э','ф','ъ','ё'];
function adaptiveLetterPool(lang) { return lang === 'ru' ? ADAPTIVE_LETTERS_RU : ADAPTIVE_LETTERS_EN; }

function adaptiveStateKey(layout, lang) { return `tt-adaptive:${layout}:${lang}`; }
function loadAdaptiveState(layout, lang) {
    try {
        const raw = localStorage.getItem(adaptiveStateKey(layout, lang));
        return raw ? JSON.parse(raw) : { unlocked: 5, mastery: {} };
    } catch { return { unlocked: 5, mastery: {} }; }
}
function saveAdaptiveState(layout, lang, state) {
    try { localStorage.setItem(adaptiveStateKey(layout, lang), JSON.stringify(state)); } catch {}
}

function buildAdaptiveText(lang, unlockedCount) {
    const pool = adaptiveLetterPool(lang).slice(0, Math.max(5, Math.min(unlockedCount, adaptiveLetterPool(lang).length)));
    const state = loadAdaptiveState(settings.layout, lang);
    // Sampling weight: inverse of mastery (less mastered = more samples).
    const weights = pool.map(l => 1.05 - (state.mastery[l] || 0));
    const totalW = weights.reduce((a, b) => a + b, 0);
    // Generate ~30 pseudo-words; 3-7 chars each.
    const out = [];
    for (let w = 0; w < 30; w++) {
        const wlen = 3 + Math.floor(Math.random() * 5);
        let word = '';
        for (let i = 0; i < wlen; i++) {
            let r = Math.random() * totalW;
            for (let j = 0; j < pool.length; j++) {
                r -= weights[j];
                if (r <= 0) { word += pool[j]; break; }
            }
            if (word.length <= i) word += pool[pool.length - 1];
        }
        out.push(word);
    }
    return out.join(' ');
}

function renderAdaptiveLetters() {
    const root = $('[data-adaptive-letters]');
    if (!root) return;
    const state = loadAdaptiveState(settings.layout, settings.language);
    const pool = adaptiveLetterPool(settings.language).slice(0, state.unlocked);
    root.innerHTML = pool.map(l => {
        const m = state.mastery[l] || 0;
        const hue = Math.round(m * 120); // 0=red → 120=green
        return `<span class="tt-adaptive-letter" style="background:hsl(${hue} 60% 35%)" title="${l}: ${Math.round(m * 100)}% mastery">${l}</span>`;
    }).join('');
}

function startAdaptiveSession() {
    cancelAnimationFrame(rafId);
    const state = loadAdaptiveState(settings.layout, settings.language);
    const text = buildAdaptiveText(settings.language, state.unlocked);
    engine.mode = 'adaptive';
    engine.submode = 'adaptive';
    engine.setText(text, { kind: 'words', value: text.split(/\s+/).length });
    renderText();
    refreshStats();
    renderAdaptiveLetters();
    dom.overlay?.classList.add('hidden');
    ensureFocus();
    rafId = requestAnimationFrame(loop);
}

function onAdaptiveComplete(m) {
    const lang = settings.language;
    const state = loadAdaptiveState(settings.layout, lang);
    // Update mastery for each touched letter.
    const pool = adaptiveLetterPool(lang).slice(0, state.unlocked);
    for (const l of pool) {
        const c = engine.perKeyCorrect[l] || 0;
        const e = engine.perKeyError[l] || 0;
        const total = c + e;
        if (total < 3) continue;
        const acc = c / total;
        // Sigmoid-ish mastery — simple EMA.
        const sample = Math.min(1, Math.max(0, acc * (m.wpm / 60)));
        const prev = state.mastery[l] || 0;
        state.mastery[l] = prev * 0.7 + sample * 0.3;
    }
    // Check unlock condition: ALL current letters mastery ≥ 0.95
    const minMast = Math.min(...pool.map(l => state.mastery[l] || 0));
    if (minMast >= 0.95 && state.unlocked < adaptiveLetterPool(lang).length) {
        state.unlocked++;
        const newLetter = adaptiveLetterPool(lang)[state.unlocked - 1];
        if (window.__toast) window.__toast(T('adaptive_unlocked', 'New letter unlocked: :letter').replace(':letter', newLetter), 'success');
        track('typing_adaptive_unlock', { letter: newLetter, total: state.unlocked });
    }
    saveAdaptiveState(settings.layout, lang, state);
    track('typing_adaptive_complete', {
        wpm: m.wpm, accuracy: m.accuracyPct,
        unlocked: state.unlocked,
        layout: settings.layout, language: lang,
    });
}

// ── Code mode ───────────────────────────────────────────────────
const CODE_SNIPPETS = {
    js: [
        "function fibonacci(n) {\n  if (n <= 1) return n;\n  return fibonacci(n - 1) + fibonacci(n - 2);\n}",
        "const debounce = (fn, ms) => {\n  let id;\n  return (...args) => {\n    clearTimeout(id);\n    id = setTimeout(() => fn(...args), ms);\n  };\n};",
        "const flatten = arr => arr.reduce((flat, cur) => flat.concat(Array.isArray(cur) ? flatten(cur) : cur), []);",
        "async function fetchUser(id) {\n  const res = await fetch(`/api/users/${id}`);\n  if (!res.ok) throw new Error('Not found');\n  return await res.json();\n}",
        "class Stack {\n  constructor() { this.items = []; }\n  push(x) { this.items.push(x); }\n  pop() { return this.items.pop(); }\n  peek() { return this.items[this.items.length - 1]; }\n  size() { return this.items.length; }\n}",
    ],
    ts: [
        "interface User { id: number; name: string; email?: string; }\nfunction greet(u: User): string {\n  return `Hello, ${u.name}!`;\n}",
        "type Result<T> = { ok: true; value: T } | { ok: false; error: string };\nasync function safe<T>(p: Promise<T>): Promise<Result<T>> {\n  try { return { ok: true, value: await p }; }\n  catch (e) { return { ok: false, error: String(e) }; }\n}",
        "const isString = (x: unknown): x is string => typeof x === 'string';\nconst items: unknown[] = [1, 'two', 3];\nconst strs = items.filter(isString);",
    ],
    py: [
        "def fibonacci(n: int) -> int:\n    if n <= 1:\n        return n\n    return fibonacci(n - 1) + fibonacci(n - 2)",
        "def quicksort(arr):\n    if len(arr) <= 1:\n        return arr\n    pivot = arr[len(arr) // 2]\n    left = [x for x in arr if x < pivot]\n    middle = [x for x in arr if x == pivot]\n    right = [x for x in arr if x > pivot]\n    return quicksort(left) + middle + quicksort(right)",
        "class Stack:\n    def __init__(self):\n        self.items = []\n    def push(self, item):\n        self.items.append(item)\n    def pop(self):\n        return self.items.pop()\n    def peek(self):\n        return self.items[-1] if self.items else None",
    ],
    go: [
        "func fibonacci(n int) int {\n    if n <= 1 {\n        return n\n    }\n    return fibonacci(n-1) + fibonacci(n-2)\n}",
        "type Stack struct {\n    items []int\n}\n\nfunc (s *Stack) Push(x int) {\n    s.items = append(s.items, x)\n}\n\nfunc (s *Stack) Pop() (int, bool) {\n    if len(s.items) == 0 {\n        return 0, false\n    }\n    x := s.items[len(s.items)-1]\n    s.items = s.items[:len(s.items)-1]\n    return x, true\n}",
        "package main\n\nimport (\n    \"fmt\"\n    \"sort\"\n)\n\nfunc main() {\n    nums := []int{3, 1, 4, 1, 5, 9, 2, 6}\n    sort.Ints(nums)\n    fmt.Println(nums)\n}",
    ],
};

function startCodeSession() {
    cancelAnimationFrame(rafId);
    const lang = settings.codeLang || 'js';
    const pool = CODE_SNIPPETS[lang] || CODE_SNIPPETS.js;
    const snippet = pool[Math.floor(Math.random() * pool.length)];
    engine.mode = 'code';
    engine.submode = `code-${lang}`;
    engine.setText(snippet, { kind: 'custom', value: snippet.length });
    renderText();
    refreshStats();
    dom.overlay?.classList.add('hidden');
    ensureFocus();
    rafId = requestAnimationFrame(loop);
}

// ── Race mode (vs AI ghost) ─────────────────────────────────────
let raceGhost = null;       // { entries: [{t, ch}], totalChars }
let raceGhostMode = 'self_best'; // self_best | self_last | bot_40 | bot_60 | bot_80
let raceLoopId = 0;

function replayKey(layout, lang) { return `tt-replays:${layout}:${lang}`; }
function loadReplays(layout, lang) {
    try {
        const raw = localStorage.getItem(replayKey(layout, lang));
        return raw ? JSON.parse(raw) : { best: null, last: null };
    } catch { return { best: null, last: null }; }
}
function saveReplays(layout, lang, replays) {
    try { localStorage.setItem(replayKey(layout, lang), JSON.stringify(replays)); } catch {}
}

function startRaceSession() {
    cancelAnimationFrame(rafId);
    clearInterval(raceLoopId);
    const text = makeWordList(settings.language, 30);
    engine.mode = 'race';
    engine.submode = `race-${raceGhostMode}`;
    engine.setText(text, { kind: 'words', value: 30 });
    renderText();
    refreshStats();

    // Load ghost
    const replays = loadReplays(settings.layout, settings.language);
    if (raceGhostMode === 'self_best' && replays.best) raceGhost = replays.best;
    else if (raceGhostMode === 'self_last' && replays.last) raceGhost = replays.last;
    else if (raceGhostMode.startsWith('bot_')) {
        const wpm = parseInt(raceGhostMode.split('_')[1], 10) || 60;
        // Synthetic ghost: chars/sec = wpm * 5 / 60
        const cps = wpm * 5 / 60;
        const entries = [];
        for (let i = 0; i < text.length; i++) entries.push({ t: i * 1000 / cps, ch: text[i] });
        raceGhost = { entries, totalChars: text.length, wpm };
    } else {
        raceGhost = null;
        if (window.__toast) window.__toast(T('race_no_replay', 'No replay yet — finish a session first.'), 'info');
    }
    if ($('[data-race-ghost-wpm]')) $('[data-race-ghost-wpm]').textContent = (raceGhost?.wpm || '—') + ' WPM';
    $('[data-race-ghost-bar]').style.width = '0%';
    $('[data-race-self-bar]').style.width = '0%';

    dom.overlay?.classList.add('hidden');
    ensureFocus();
    rafId = requestAnimationFrame(loop);

    // Ghost-advancement loop (every 50ms)
    const ghostStart = performance.now();
    raceLoopId = setInterval(() => {
        if (!raceGhost || engine.ended) { clearInterval(raceLoopId); return; }
        const elapsed = performance.now() - ghostStart;
        let ghostPos = 0;
        for (const e of raceGhost.entries) {
            if (e.t <= elapsed) ghostPos++;
            else break;
        }
        const ghostPct = Math.min(100, 100 * ghostPos / engine.text.length);
        $('[data-race-ghost-bar]').style.width = ghostPct + '%';
        const selfPct = Math.min(100, 100 * engine.cursor / engine.text.length);
        $('[data-race-self-bar]').style.width = selfPct + '%';
        const m = engine.metrics();
        if ($('[data-race-self-wpm]')) $('[data-race-self-wpm]').textContent = m.wpm + ' WPM';
    }, 50);
}

function onRaceComplete(m) {
    clearInterval(raceLoopId);
    const lang = settings.language;
    const replays = loadReplays(settings.layout, lang);
    const replay = {
        entries: engine.keystrokes.filter(k => k.ok).map(k => ({ t: k.t, ch: k.ch })),
        totalChars: engine.text.length,
        wpm: m.wpm,
    };
    replays.last = replay;
    if (!replays.best || m.wpm > replays.best.wpm) {
        replays.best = replay;
        if (window.__toast) window.__toast(T('race_you_won', 'You won! 🏆'), 'success');
    }
    saveReplays(settings.layout, lang, replays);
    track('typing_race_complete', { wpm: m.wpm, ghost_mode: raceGhostMode, layout: settings.layout, language: lang });
}

// ── Restart helper ──────────────────────────────────────────────
function restartCurrentMode() {
    if (currentMode === 'lessons') {
        if (currentLessonId) startLesson(currentLessonId);
        else renderLessonsGrid();
    } else if (currentMode === 'adaptive') startAdaptiveSession();
    else if (currentMode === 'code')      startCodeSession();
    else if (currentMode === 'race')      startRaceSession();
    else startSession();
}

// ── Certificate (jsPDF by click: our own copy first) ──
// The chain comes from the blade (cdn_chain(): own copy → own second address →
// jsdelivr last) and goes through the shared window.__vendorScript. Until
// 17.10.2026 it was __CDN.script('jsdelivr', jspdf@2.5.1): intl went to jsdelivr
// first, ru to Beget's proxy, and with the CDN unreachable the certificate was
// never generated (bench: 4 of 4 failed) — while /vendor/jspdf (4.2.1) sat on
// both origins. docs/why/primitives/vendor-own-copy.md.
async function loadJsPDF() {
    if (window.jspdf?.jsPDF) return window.jspdf.jsPDF;
    const src = window.TT_JSPDF_SRC || '/vendor/jspdf/jspdf.umd.min.js';
    try {
        if (typeof window.__vendorScript === 'function') {
            await window.__vendorScript(src, { expect: () => !!(window.jspdf && window.jspdf.jsPDF), label: 'jspdf' });
        } else {
            await new Promise((res, rej) => {
                const s = document.createElement('script');
                s.src = Array.isArray(src) ? src[0] : src;
                s.onload = res;
                s.onerror = rej;
                document.head.appendChild(s);
            });
        }
    } catch (e) {
        throw new Error('Failed to load jsPDF');
    }
    return window.jspdf?.jsPDF;
}

async function onGetCertificate() {
    if (!lastResultMetrics) return;
    const m = lastResultMetrics;
    const btn = $('[data-tt-get-certificate]');
    if (btn) { btn.disabled = true; btn.textContent = '…'; }

    try {
        // 1. Issue cert via server (gets signed hash + verify URL)
        const csrf = document.querySelector('meta[name=csrf-token]')?.content;
        const res = await fetch('/api/typing-certificate/issue', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf, 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
                wpm: m.wpm, accuracy_pct: m.accuracyPct, duration_sec: m.durationSec,
                chars: m.chars, correct: m.correct,
                layout: settings.layout, language: settings.language, mode: engine.mode,
            }),
        });
        if (!res.ok) throw new Error('Issue failed: ' + res.status);
        const cert = await res.json();
        trackCritical('typing_certificate_issued', { hash: cert.hash, wpm: m.wpm });

        // 2. Load jsPDF and generate PDF
        const JsPDF = await loadJsPDF();
        const pdf = new JsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

        pdf.setFillColor(15, 23, 42);
        pdf.rect(0, 0, 297, 210, 'F');

        // Title
        pdf.setTextColor(56, 189, 248);
        pdf.setFontSize(36);
        pdf.setFont('helvetica', 'bold');
        pdf.text('TIMBRICA', 148.5, 35, { align: 'center' });

        pdf.setTextColor(229, 231, 235);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Typing Certificate', 148.5, 45, { align: 'center' });

        // Holder name
        pdf.setFontSize(28);
        pdf.setFont('helvetica', 'bold');
        pdf.text(cert.displayName, 148.5, 82, { align: 'center' });

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(148, 163, 184);
        pdf.text('has demonstrated touch-typing proficiency at', 148.5, 95, { align: 'center' });

        // Score
        pdf.setFontSize(48);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(34, 197, 94);
        pdf.text(`${m.wpm} WPM`, 148.5, 122, { align: 'center' });

        pdf.setFontSize(14);
        pdf.setTextColor(229, 231, 235);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`at ${m.accuracyPct}% accuracy · ${m.durationSec}s session`, 148.5, 134, { align: 'center' });

        pdf.text(`Layout: ${settings.layout.toUpperCase()} · Language: ${settings.language.toUpperCase()}`, 148.5, 144, { align: 'center' });

        // Verify URL
        pdf.setFontSize(10);
        pdf.setTextColor(56, 189, 248);
        pdf.text(`Verify: ${cert.verifyUrl}`, 148.5, 172, { align: 'center' });

        pdf.setFontSize(8);
        pdf.setTextColor(100, 116, 139);
        pdf.text(`Issued: ${new Date(cert.issuedAt).toLocaleDateString()} · Hash: ${cert.hash}`, 148.5, 180, { align: 'center' });

        window.__savePdf(pdf, `Timbrica-Typing-Certificate-${cert.hash}.pdf`);
        track('typing_certificate_downloaded', { hash: cert.hash });

        if (window.__toast) window.__toast(T('cert_downloaded', 'Certificate downloaded. Verify URL: :url').replace(':url', cert.verifyUrl), 'success');
    } catch (e) {
        console.error('[typing-trainer] cert error', e);
        if (window.__handleError) window.__handleError(e, 'typing-trainer:cert');
        else if (window.__toast) window.__toast(T('cert_failed', 'Could not generate the certificate: :msg').replace(':msg', e.message), 'error');
    } finally {
        if (btn) { btn.disabled = false; btn.textContent = '📜 ' + T('get_certificate', 'Get Certificate'); }
    }
}

// ── Mode tabs ───────────────────────────────────────────────────
let currentMode = 'free';
function selectMode(mode) {
    if (currentMode === mode) return;
    cancelAnimationFrame(rafId);
    clearInterval(raceLoopId);
    const fromMode = currentMode;
    currentMode = mode;
    $$('.tt-mode-tab').forEach(t => t.setAttribute('aria-selected', String(t.dataset.mode === mode)));
    $$('[data-mode-panel]').forEach(p => { p.hidden = (p.dataset.modePanel !== mode); });

    // Mode-specific UI toggles
    const stage = $('[data-tt-stage]');
    const lessonsGrid = $('[data-lessons-grid-wrap]');
    const lessonBanner = $('[data-lesson-active-banner]');
    const adaptiveHeader = $('[data-adaptive-header]');
    const raceLanes = $('[data-race-lanes]');

    // Custom textarea (Free Practice's "custom" submode) belongs to Free only.
    const customTa = $('[data-custom-text]');
    if (customTa) customTa.hidden = (mode !== 'free' || settings.submode !== 'custom');

    if (mode === 'lessons') {
        stage.hidden = true;
        lessonsGrid.hidden = false;
        lessonBanner.hidden = true;
        adaptiveHeader.hidden = true;
        raceLanes.hidden = true;
        currentLessonId = null;
        renderLessonsGrid();
    } else if (mode === 'adaptive') {
        stage.hidden = false;
        lessonsGrid.hidden = true;
        lessonBanner.hidden = true;
        adaptiveHeader.hidden = false;
        raceLanes.hidden = true;
        startAdaptiveSession();
    } else if (mode === 'code') {
        stage.hidden = false;
        lessonsGrid.hidden = true;
        lessonBanner.hidden = true;
        adaptiveHeader.hidden = true;
        raceLanes.hidden = true;
        startCodeSession();
    } else if (mode === 'race') {
        stage.hidden = false;
        lessonsGrid.hidden = true;
        lessonBanner.hidden = true;
        adaptiveHeader.hidden = true;
        raceLanes.hidden = false;
        startRaceSession();
    } else {
        // free
        stage.hidden = false;
        lessonsGrid.hidden = true;
        lessonBanner.hidden = true;
        adaptiveHeader.hidden = true;
        raceLanes.hidden = true;
        startSession();
    }

    track('typing_mode_changed', { from_mode: fromMode, to_mode: mode });
}

// ── Submode chips wiring ────────────────────────────────────────
function setSubmode(sm) {
    settings.submode = sm;
    saveSetting('submode', sm);
    $$('[data-submode]').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.submode === sm)));
    $$('[data-submode-opts]').forEach(g => g.hidden = (g.dataset.submodeOpts !== sm));
    $('[data-custom-text]').hidden = (sm !== 'custom');
    startSession();
}

function setTime(v)  { settings.timeSec = String(v); saveSetting('timeSec', String(v)); $$('[data-time]').forEach(c => c.setAttribute('aria-pressed', String(+c.dataset.time === +v))); startSession(); }
function setWords(v) { settings.wordsN = String(v); saveSetting('wordsN', String(v)); $$('[data-words]').forEach(c => c.setAttribute('aria-pressed', String(+c.dataset.words === +v))); startSession(); }
function setQuote(v) { settings.quoteLen = v; saveSetting('quoteLen', v); $$('[data-quote]').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.quote === v))); startSession(); }

// ── Bootstrap ───────────────────────────────────────────────────
function bootstrap() {
    dom.app    = $('[data-tt-app]');
    if (!dom.app) return;
    dom.text   = $('[data-typing-text]');
    dom.input  = $('[data-typing-input]');
    dom.overlay = $('[data-typing-overlay]');
    dom.stage  = $('[data-tt-stage]');
    dom.kbdNote = $('[data-tt-kbd-note]');
    dom.resultsDialog = $('[data-tt-results]');
    dom.settingsDialog = $('[data-tt-settings-dialog]');

    loadSettings();

    // Restore UI to settings
    $$('[data-submode]').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.submode === settings.submode)));
    $$('[data-submode-opts]').forEach(g => g.hidden = (g.dataset.submodeOpts !== settings.submode));
    $$('[data-time]').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.time === settings.timeSec)));
    $$('[data-words]').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.words === settings.wordsN)));
    $$('[data-quote]').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.quote === settings.quoteLen)));
    $('[data-custom-text]').hidden = (settings.submode !== 'custom');

    // Settings dialog values
    $$('[data-setting]').forEach(sel => {
        const key = sel.dataset.setting;
        if (settings[key] !== undefined) sel.value = String(settings[key]);
        sel.addEventListener('change', () => {
            saveSetting(key, sel.value);
            if (key === 'language') {
                // Lazy-load content for the new locale, then start a fresh session.
                loadContent(sel.value).finally(() => startSession());
            }
            track('typing_settings_changed', { key, value: sel.value });
        });
    });

    // Mode tabs
    $$('.tt-mode-tab').forEach(tab => tab.addEventListener('click', () => {
        if (tab.hasAttribute('disabled')) return;
        selectMode(tab.dataset.mode);
    }));

    // Submode chips
    $$('[data-submode]').forEach(c => c.addEventListener('click', () => setSubmode(c.dataset.submode)));
    $$('[data-time]').forEach(c => c.addEventListener('click', () => setTime(c.dataset.time)));
    $$('[data-words]').forEach(c => c.addEventListener('click', () => setWords(c.dataset.words)));
    $$('[data-quote]').forEach(c => c.addEventListener('click', () => setQuote(c.dataset.quote)));

    // Custom text changes
    $('[data-custom-text]').addEventListener('change', () => { if (settings.submode === 'custom') startSession(); });

    // Toolbar
    $('[data-tt-restart]').addEventListener('click', restartCurrentMode);
    $('[data-tt-settings]').addEventListener('click', () => {
        const d = dom.settingsDialog;
        if (d && typeof d.showModal === 'function') {
            try { d.showModal(); } catch (_) { d.setAttribute('open', 'open'); }
        }
    });
    $('[data-tt-close-settings]')?.addEventListener('click', () => dom.settingsDialog?.close());
    $('[data-tt-try-again]')?.addEventListener('click', () => { dom.resultsDialog?.close(); restartCurrentMode(); });
    // Close after a lesson returns to the grid so the freshly-unlocked next
    // tile is visible; other modes just dismiss the modal in place.
    $('[data-tt-close-results]')?.addEventListener('click', () => {
        dom.resultsDialog?.close();
        if (engine.mode === 'lessons') backToLessonsGrid();
    });
    // Next-lesson CTA — jump straight into the successor lesson.
    $('[data-tt-next-lesson]')?.addEventListener('click', () => {
        const id = nextLessonId;
        dom.resultsDialog?.close();
        if (id != null) startLesson(id);
    });

    // Lessons back-to-grid
    $('[data-tt-back-to-lessons]')?.addEventListener('click', backToLessonsGrid);

    // Code-mode chips
    $$('[data-code-lang]').forEach(c => c.addEventListener('click', () => {
        const lang = c.dataset.codeLang;
        settings.codeLang = lang;
        saveSetting('codeLang', lang);
        $$('[data-code-lang]').forEach(x => x.setAttribute('aria-pressed', String(x.dataset.codeLang === lang)));
        if (currentMode === 'code') startCodeSession();
    }));
    // Race-mode chips
    $$('[data-race-ghost]').forEach(c => c.addEventListener('click', () => {
        raceGhostMode = c.dataset.raceGhost;
        $$('[data-race-ghost]').forEach(x => x.setAttribute('aria-pressed', String(x.dataset.raceGhost === raceGhostMode)));
        if (currentMode === 'race') startRaceSession();
    }));

    // Certificate button — lazy-load jsPDF and generate
    $('[data-tt-get-certificate]')?.addEventListener('click', onGetCertificate);

    // Stage focus — click works for mouse, touchstart for taps on iOS where
    // click can be delayed/cancelled.
    dom.stage.addEventListener('click', ensureFocus);
    dom.stage.addEventListener('focus', ensureFocus);
    dom.stage.addEventListener('pointerdown', ensureFocus, { passive: true });
    dom.stage.addEventListener('touchstart', ensureFocus, { passive: true });

    // This page IS a keyboard tool — it captures every printable key at the
    // document level (onKeydown, capture phase) for the whole lifetime. Declare
    // that ownership the same way games do (public/js/car-racing/index.js) so
    // the site-wide hotkey logger (layouts/app.blade.php) and the core Space/
    // Escape shortcut handler (timbrica-core.js) both step aside. Without this,
    // every space/letter typed here was double-handled and logged as a spurious
    // `feature_use{action:hotkey}` row — 2252 of them in two days of telemetry,
    // swamping real signal. Set once; the trainer never relinquishes the keys.
    try { document.body.setAttribute('data-kbd-capture', '1'); } catch (_) {}

    // Input listeners — keydown is the primary path for physical keyboards.
    // Input is the fallback for soft-keyboards (Android, iOS), IME composition.
    // On WINDOW, capture phase (17.10.2026): the layout's guard for Space in a text field
    // (__keyOwnedByFocus, document capture, registered while the page parses) stopped the
    // Space keydown before this deferred listener on document ever saw it, so a space only
    // arrived through onInputFallback — which drops input within 60 ms of the previous
    // keydown. Lab (real clock, 120 chars): keys 25 ms apart — 21 of 24 spaces lost and the
    // text shifted (101 of 120 wrong), 45 ms — 18 of 25; on window capture — 0 at any pace,
    // Chromium and WebKit. Window capture runs before document capture.
    window.addEventListener('keydown', onKeydown, true);
    if (dom.input) {
        dom.input.addEventListener('compositionstart', onCompositionStart);
        dom.input.addEventListener('compositionend', onCompositionEnd);
        dom.input.addEventListener('input', onInputFallback);
        dom.input.addEventListener('keyup', () => { /* keep input value emptied */ if (dom.input.value) dom.input.value = ''; });
    }
    // Initial keyboard render
    renderKeyboard();
    const kbdRoot = $('[data-tt-keyboard]');
    if (kbdRoot) kbdRoot.addEventListener('mousedown', keepFocusOffTheKeys);

    // Lazy-load full content for the active locale (fall back to inline pool if fetch fails).
    loadContent(settings.language).then(() => {
        // If user is still on the first generated text, regenerate with richer pool.
        if (engine.cursor === 0 && !engine.running && currentMode === 'free') startSession();
    });

    // Re-render keyboard when layout changes via settings
    document.addEventListener('change', e => {
        if (e.target?.dataset?.setting === 'layout') {
            settings.layout = e.target.value;
            renderKeyboard();
        }
    });

    // Telemetry funnel — page view event (separate from session_start which
    // only fires on first keystroke). Captures all visitors, including
    // bouncers who never start typing.
    if (window.Timbrica3D?.init) window.Timbrica3D.init(SLUG);
    track('typing_page_view', {
        mode: 'free',
        submode: settings.submode,
        layout: settings.layout,
        language: settings.language,
        is_touch: matchMedia('(pointer: coarse)').matches,
        viewport_w: window.innerWidth,
        viewport_h: window.innerHeight,
    });

    // Mark whether the user abandons without ever typing — fires on
    // visibility-hidden / pagehide if no session was started.
    let sessionEverStarted = false;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden' && !sessionEverStarted) {
            track('typing_page_abandon_no_start', { time_on_page_ms: Math.round(performance.now()) });
        }
    });
    const origStart = engine._start.bind(engine);
    engine._start = function () { sessionEverStarted = true; return origStart(); };

    // First text
    startSession();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootstrap);
else bootstrap();

})();
